import type { FmsNotificationPayload } from 'hooks/useFmsNotifications'
import type { NotificationReadContext } from 'utils/notificationClient'

/** Legacy local read cache; cleared after server-side read state shipped. */
const LEGACY_READ_IDS_STORAGE_KEY = 'fms_notifications_read_v1'

export type NotificationHistoryItem = {
  id: number
  event_type: string
  title: string
  body: string
  severity: string
  read?: boolean
  read_at?: string | null
  robot_id?: number | null
  robot_name?: string | null
  robot_serial?: string | null
  robot_color?: string | null
  station_pk?: number | null
  station_id?: string | null
  station_name?: string | null
  odyssey_id?: number | null
  created_at?: string
}

export type NotificationHistoryPage = {
  results: NotificationHistoryItem[]
  total: number
  page_number: number
  page_size: number
  page_next: string | null
  page_previous: string | null
}

type PaginatedEvents = {
  results?: NotificationHistoryItem[]
  total?: number
  page_number?: number
  page_size?: number
  page_next?: string | null
  page_previous?: string | null
}

function clearLegacyReadCache(): void {
  try {
    localStorage.removeItem(LEGACY_READ_IDS_STORAGE_KEY)
  } catch {
    /* ignore */
  }
}

async function postJson(url: string, body?: object): Promise<Response> {
  return fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  })
}

function buildEventsQuery(params: {
  page?: number
  pageSize?: number
  eventType?: string
  notifyGroupId?: number
  search?: string
  robotId?: number
  stationPk?: number
  /** When set, history is limited to these robots OR these stations (favorites mode). */
  robotIds?: number[]
  stationPks?: number[]
  errorGroup?: string
  createdAtAfter?: string
  createdAtBefore?: string
}): string {
  const qs = new URLSearchParams()
  if (params.page != null) {
    qs.set('page', String(params.page))
  }
  if (params.pageSize != null) {
    qs.set('page_size', String(params.pageSize))
  }
  if (params.notifyGroupId != null && params.notifyGroupId > 0) {
    qs.set('notify_group_id', String(params.notifyGroupId))
  } else if (params.eventType) {
    qs.set('event_type', params.eventType)
  }
  if (params.robotIds || params.stationPks) {
    qs.set('robot_ids', (params.robotIds ?? []).join(','))
    qs.set('station_pks', (params.stationPks ?? []).join(','))
  } else {
    if (params.robotId != null && params.robotId > 0) {
      qs.set('robot_id', String(params.robotId))
    }
    if (params.stationPk != null && params.stationPk > 0) {
      qs.set('station_pk', String(params.stationPk))
    }
  }
  if (params.errorGroup?.trim()) {
    qs.set('error_group', params.errorGroup.trim())
  }
  if (params.search?.trim()) {
    qs.set('search', params.search.trim())
  }
  if (params.createdAtAfter) {
    qs.set('created_at_after', params.createdAtAfter)
  }
  if (params.createdAtBefore) {
    qs.set('created_at_before', params.createdAtBefore)
  }
  const query = qs.toString()
  return query ? `?${query}` : ''
}

export async function fetchNotificationEvents(): Promise<FmsNotificationPayload[]> {
  const page = await fetchNotificationEventsPage({ page: 1, pageSize: 100 })
  return page.results as FmsNotificationPayload[]
}

export async function fetchNotificationEventsPage(params: {
  page?: number
  pageSize?: number
  eventType?: string
  notifyGroupId?: number
  search?: string
  robotId?: number
  stationPk?: number
  /** When set, history is limited to these robots OR these stations (favorites mode). */
  robotIds?: number[]
  stationPks?: number[]
  errorGroup?: string
  createdAtAfter?: string
  createdAtBefore?: string
}): Promise<NotificationHistoryPage> {
  clearLegacyReadCache()
  const query = buildEventsQuery(params)
  const res = await fetch(`/api/notifications/events/${query}`, { credentials: 'include' })
  if (!res.ok) {
    throw new Error(`Failed to load notifications: ${res.status}`)
  }
  const data = (await res.json()) as NotificationHistoryItem[] | PaginatedEvents
  if (Array.isArray(data)) {
    return {
      results: data,
      total: data.length,
      page_number: 1,
      page_size: data.length,
      page_next: null,
      page_previous: null,
    }
  }
  return {
    results: data.results ?? [],
    total: data.total ?? 0,
    page_number: data.page_number ?? 1,
    page_size: data.page_size ?? params.pageSize ?? 20,
    page_next: data.page_next ?? null,
    page_previous: data.page_previous ?? null,
  }
}

export type NotificationFilterRobot = {
  id: number
  name: string
  serial_number: string
}

export type NotificationFilterAsset = {
  kind: 'robot' | 'station'
  id: number
  name: string
  serial_number: string
}

export async function fetchNotificationFilterRobots(): Promise<NotificationFilterRobot[]> {
  const res = await fetch('/api/notifications/events/filter-robots/', { credentials: 'include' })
  if (!res.ok) {
    throw new Error(`Failed to load notification robots: ${res.status}`)
  }
  const data = (await res.json()) as NotificationFilterRobot[]
  return Array.isArray(data) ? data : []
}

export async function fetchNotificationFilterAssets(): Promise<NotificationFilterAsset[]> {
  const res = await fetch('/api/notifications/events/filter-assets/', { credentials: 'include' })
  if (!res.ok) {
    throw new Error(`Failed to load notification assets: ${res.status}`)
  }
  const data = (await res.json()) as NotificationFilterAsset[]
  return Array.isArray(data) ? data : []
}

export async function markNotificationReadOnServer(id: number, context: NotificationReadContext): Promise<void> {
  const res = await postJson(`/api/notifications/events/${id}/read/`, context)
  if (!res.ok) {
    throw new Error(`Failed to mark notification read: ${res.status}`)
  }
}

export async function markAllNotificationsReadOnServer(context: NotificationReadContext): Promise<void> {
  const res = await postJson('/api/notifications/events/mark-all-read/', context)
  if (!res.ok) {
    throw new Error(`Failed to mark all notifications read: ${res.status}`)
  }
}

export async function markNotificationsReadBulkOnServer(
  filter: {
    ids?: number[]
    robot_id?: number
    odyssey_id?: number
  },
  context: NotificationReadContext
): Promise<void> {
  const res = await postJson('/api/notifications/events/mark-read/', { ...filter, ...context })
  if (!res.ok) {
    throw new Error(`Failed to mark notifications read: ${res.status}`)
  }
}

export function sortNotificationsByTime(a: FmsNotificationPayload, b: FmsNotificationPayload): number {
  const ta = a.created_at ? Date.parse(a.created_at) : 0
  const tb = b.created_at ? Date.parse(b.created_at) : 0
  return tb - ta
}
